import { useState, useEffect } from 'react';
import { X, ZoomIn, ZoomOut, Download, Printer, RotateCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { IntakePhoto } from '@/api/intakeApi';

interface PhotoViewerProps {
  photos: IntakePhoto[];
  initialIndex: number;
  onClose: () => void;
}

export function PhotoViewer({ photos, initialIndex, onClose }: PhotoViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  const photo = photos[currentIndex];
  const htmlEncode = (value: string) => value.replace(/[&<>'"]/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  }[character] ?? character));

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && currentIndex > 0) setCurrentIndex(currentIndex - 1);
      if (e.key === 'ArrowRight' && currentIndex < photos.length - 1) setCurrentIndex(currentIndex + 1);
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, photos.length, onClose]);

  function handleDownload() {
    const link = document.createElement('a');
    link.href = photo.url;
    link.download = photo.fileName || `photo-${currentIndex + 1}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function handlePrint() {
    const printWindow = window.open('', '', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print Photo</title>
            <style>
              body { margin: 0; padding: 20px; background: white; font-family: Arial, sans-serif; }
              img { max-width: 100%; max-height: 90vh; display: block; margin: 0 auto; }
              .meta { margin-top: 20px; font-size: 12px; color: #666; }
            </style>
          </head>
          <body>
            <img src="${htmlEncode(photo.url)}" alt="Customer intake photo" />
            <div class="meta">
              <p><strong>File:</strong> ${htmlEncode(photo.fileName)}</p>
              <p><strong>Uploaded:</strong> ${htmlEncode(new Date(photo.uploadedAt).toLocaleString())}</p>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => printWindow.print(), 250);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/95 z-[9999] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/50 border-b border-white/10">
        <div className="flex-1">
          <p className="text-white text-sm font-medium">
            {currentIndex + 1} / {photos.length}
          </p>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setZoom(Math.max(0.5, zoom - 0.2))}
            title="Zoom out"
            className="p-2 hover:bg-white/10 rounded-lg text-white transition-colors"
          >
            <ZoomOut className="h-5 w-5" />
          </button>

          <div className="text-white text-sm min-w-[3rem] text-center">
            {Math.round(zoom * 100)}%
          </div>

          <button
            onClick={() => setZoom(Math.min(3, zoom + 0.2))}
            title="Zoom in"
            className="p-2 hover:bg-white/10 rounded-lg text-white transition-colors"
          >
            <ZoomIn className="h-5 w-5" />
          </button>

          <div className="w-px h-6 bg-white/20" />

          <button
            onClick={() => setRotation((rotation + 90) % 360)}
            title="Rotate 90°"
            className="p-2 hover:bg-white/10 rounded-lg text-white transition-colors"
          >
            <RotateCw className="h-5 w-5" />
          </button>

          <button
            onClick={handleDownload}
            title="Download photo"
            className="p-2 hover:bg-blue-500/20 rounded-lg text-blue-400 transition-colors"
          >
            <Download className="h-5 w-5" />
          </button>

          <button
            onClick={handlePrint}
            title="Print photo"
            className="p-2 hover:bg-green-500/20 rounded-lg text-green-400 transition-colors"
          >
            <Printer className="h-5 w-5" />
          </button>

          <div className="w-px h-6 bg-white/20" />

          <button
            onClick={onClose}
            title="Close"
            className="p-2 hover:bg-red-500/20 rounded-lg text-red-400 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Image container */}
      <div className="flex-1 flex items-center justify-center overflow-hidden relative">
        <img
          src={photo.url}
          alt={`Photo ${currentIndex + 1}`}
          className="object-contain cursor-move transition-transform"
          style={{
            transform: `scale(${zoom}) rotate(${rotation}deg)`,
            maxHeight: '100%',
            maxWidth: '100%',
          }}
        />
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/50 border-t border-white/10">
        <button
          onClick={() => currentIndex > 0 && setCurrentIndex(currentIndex - 1)}
          disabled={currentIndex === 0}
          title="Previous photo (←)"
          className="p-2 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg text-white transition-colors"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        <p className="text-white/60 text-sm flex-1 text-center">
          {photo.fileName}
        </p>

        <button
          onClick={() => currentIndex < photos.length - 1 && setCurrentIndex(currentIndex + 1)}
          disabled={currentIndex === photos.length - 1}
          title="Next photo (→)"
          className="p-2 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg text-white transition-colors"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Keyboard hint */}
      <div className="text-center py-2 text-xs text-white/40">
        Use ← → to navigate • ESC to close • +/- to zoom • ⟲ to rotate
      </div>
    </div>
  );
}
